/**
 * Desktop side-rail width — the user's choice, remembered.
 *
 * WHY THIS EXISTS. The rail was a fixed `lg:w-60` (240px) and the item label is
 * `overflow-hidden whitespace-nowrap`, so "Hotel Supplier Management" rendered as
 * "Hotel Supplier Manage…". A truncated nav label is a bad trade in both
 * directions: widen it for everyone and short-label surfaces waste horizontal
 * space; leave it and the longest module in the product is unreadable.
 *
 * So: a DEFAULT that fits the longest real label, and a drag handle for anyone who
 * wants it narrower or wider. Persisted per browser, because a width you have to
 * re-drag every visit is worse than a fixed one.
 *
 * MOBILE IS UNAFFECTED. Below `lg` the rail is an off-canvas drawer at a fixed
 * `w-72` — resizing a drawer you swipe open is meaningless, and the handle is
 * `hidden lg:block`. This hook only feeds the `lg:` width.
 */
import { useCallback, useEffect, useState } from 'react';

/**
 * 272px. Chosen to fit the longest label in the platform IA — "Hotel Supplier
 * Management" at 14px Inter — plus the icon (24), its gap (12), the item's px-4
 * (32) and the rail's px-3 (24). If a longer module is ever added, this is the
 * number to revisit; the resize handle means nobody is stuck meanwhile.
 */
export const RAIL_WIDTH_DEFAULT = 272;
/** Narrow enough to reclaim space, wide enough that icons + a short label fit. */
export const RAIL_WIDTH_MIN = 200;
/** Past this the rail is competing with the content it exists to navigate. */
export const RAIL_WIDTH_MAX = 420;
/** Keyboard nudge per arrow press. */
export const RAIL_WIDTH_STEP = 16;

const STORAGE_KEY = 'andavo:shell:rail-width';

export function clampRailWidth(px: number): number {
  if (!Number.isFinite(px)) return RAIL_WIDTH_DEFAULT;
  return Math.min(RAIL_WIDTH_MAX, Math.max(RAIL_WIDTH_MIN, Math.round(px)));
}

/**
 * Read the stored width. Guarded for SSR and for browsers where localStorage
 * throws (Safari private mode) — a preference read must never break the shell.
 */
function readStored(): number {
  if (typeof window === 'undefined') return RAIL_WIDTH_DEFAULT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return RAIL_WIDTH_DEFAULT;
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? RAIL_WIDTH_DEFAULT : clampRailWidth(parsed);
  } catch {
    return RAIL_WIDTH_DEFAULT;
  }
}

export type RailWidthApi = {
  width: number;
  /** Set + persist, clamped. */
  setWidth: (px: number) => void;
  /** Back to the default that fits the longest label. */
  reset: () => void;
  isDragging: boolean;
  /** Wire onto the drag handle. */
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
};

export function useRailWidth(): RailWidthApi {
  // Initialised lazily so the stored value is read once, on mount, not per render.
  const [width, setWidthState] = useState<number>(readStored);
  const [isDragging, setIsDragging] = useState(false);

  const setWidth = useCallback((px: number) => {
    const next = clampRailWidth(px);
    setWidthState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // Preference persistence is best-effort; the session still works without it.
    }
  }, []);

  const reset = useCallback(() => setWidth(RAIL_WIDTH_DEFAULT), [setWidth]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // Left button only; ignore right-click / middle-click.
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Track the pointer on WINDOW rather than the handle, so a fast drag that
  // outruns the 4px-wide handle keeps resizing instead of stopping dead.
  useEffect(() => {
    if (!isDragging) return;

    const onMove = (e: PointerEvent) => {
      // The rail starts at the viewport's left edge, so clientX IS the width.
      setWidth(e.clientX);
    };
    const stop = () => setIsDragging(false);

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    // A drag interrupted by losing the pointer (tab switch, context menu) must
    // not leave the rail stuck in drag state.
    window.addEventListener('pointercancel', stop);
    // Suppress text selection across the app while dragging.
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging, setWidth]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      // Keyboard-operable by design: a drag-only control is unusable for anyone
      // not using a mouse, and this is primary navigation.
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          setWidth(width - RAIL_WIDTH_STEP);
          break;
        case 'ArrowRight':
          e.preventDefault();
          setWidth(width + RAIL_WIDTH_STEP);
          break;
        case 'Home':
          e.preventDefault();
          setWidth(RAIL_WIDTH_MIN);
          break;
        case 'End':
          e.preventDefault();
          setWidth(RAIL_WIDTH_MAX);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          reset();
          break;
        default:
          break;
      }
    },
    [width, setWidth, reset],
  );

  return { width, setWidth, reset, isDragging, onPointerDown, onKeyDown };
}
